// -----------------------------------------------------------------------------
// fetch wrapper for the DocuSense API.
//
// Responsibilities:
//   - Inject `Authorization: Bearer <jwt>` when a token is present in TokenStore.
//   - Parse and normalize ProblemDetails (401, 404, 422) and the bespoke
//     license error bodies (402, 403).
//   - Surface 503 + Retry-After to upstream consumers (React Query retries).
//   - Translate every non-2xx into a typed ApiError; 2xx returns parsed JSON
//     (or null on 204).
//
// This module is intentionally framework-agnostic — React Query and any future
// consumer share the same single fetch surface.
// -----------------------------------------------------------------------------

import { TokenStore } from '../auth/TokenStore';
import type {
  AuthErrorResponse,
  LicenseFeatureUnavailable,
  LicenseLimitExceeded,
  ProblemDetails,
} from './types';

export const AUTH_EXPIRED_EVENT = 'docusense:auth-expired' as const;

/** Discriminator for typed branches in caller code. */
export type ApiErrorKind =
  | 'auth'
  | 'license_limit'
  | 'license_feature'
  | 'validation'
  | 'not_found'
  | 'unavailable'
  | 'forbidden'
  | 'network'
  | 'unknown';

export interface ApiErrorDetail {
  kind: ApiErrorKind;
  /** Backend-provided machine code where available (`license_limit_exceeded`, etc.). */
  code?: string;
  /** Field-level errors from a 422 ProblemDetails response. */
  validationErrors?: Record<string, string[]>;
  /** Retry-After seconds from a 503 response, when present. */
  retryAfterSeconds?: number;
  /** Numeric license usage hints from 402. */
  limit?: number;
  current?: number;
  /** Raw payload for debugging — never log this if it may contain PII. */
  raw?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly detail: ApiErrorDetail;

  constructor(status: number, message: string, detail: ApiErrorDetail) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  /** Object/array body — will be JSON-stringified automatically. */
  json?: unknown;
  /** Raw body — used for multipart uploads (Wave 5). */
  body?: BodyInit;
  /** When true, omit the Authorization header even if a token exists. */
  skipAuth?: boolean;
}

function resolveBaseUrl(): string {
  const raw = import.meta.env.VITE_API_BASE_URL?.trim() ?? '';
  if (raw === '') {
    return ''; // same-origin fallback (e.g. Static Web Apps with /api routing).
  }
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const base = resolveBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function dispatchAuthExpired(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

async function readErrorBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    try {
      const text = await response.text();
      return text.length > 0 ? text : undefined;
    } catch {
      return undefined;
    }
  }
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function isProblemDetails(payload: unknown): payload is ProblemDetails {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    ('title' in payload || 'detail' in payload || 'errors' in payload)
  );
}

function isLicenseLimit(payload: unknown): payload is LicenseLimitExceeded {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'limit' in payload &&
    'current' in payload &&
    'code' in payload
  );
}

function isLicenseFeature(payload: unknown): payload is LicenseFeatureUnavailable {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'code' in payload &&
    'message' in payload &&
    !('limit' in payload)
  );
}

function isAuthError(payload: unknown): payload is AuthErrorResponse {
  return typeof payload === 'object' && payload !== null && 'error' in payload;
}

function buildApiError(status: number, payload: unknown, response: Response): ApiError {
  if (status === 401) {
    const code = isAuthError(payload) ? payload.error : 'unauthorized';
    return new ApiError(401, 'Unauthorized', { kind: 'auth', code, raw: payload });
  }

  if (status === 402 && isLicenseLimit(payload)) {
    return new ApiError(402, payload.message, {
      kind: 'license_limit',
      code: payload.code,
      limit: payload.limit,
      current: payload.current,
      raw: payload,
    });
  }

  if (status === 403) {
    if (isLicenseFeature(payload) && payload.code === 'license_feature_unavailable') {
      return new ApiError(403, payload.message, {
        kind: 'license_feature',
        code: payload.code,
        raw: payload,
      });
    }
    const message = isProblemDetails(payload) ? (payload.detail ?? payload.title ?? 'Forbidden') : 'Forbidden';
    return new ApiError(403, message, { kind: 'forbidden', raw: payload });
  }

  if (status === 404) {
    const message = isProblemDetails(payload) ? (payload.detail ?? payload.title ?? 'Not Found') : 'Not Found';
    return new ApiError(404, message, { kind: 'not_found', raw: payload });
  }

  if (status === 422 && isProblemDetails(payload)) {
    return new ApiError(422, payload.detail ?? payload.title ?? 'Validation failed', {
      kind: 'validation',
      ...(payload.errors !== undefined ? { validationErrors: payload.errors } : {}),
      raw: payload,
    });
  }

  if (status === 503) {
    const retryHeader = response.headers.get('retry-after');
    const retryAfterSeconds = retryHeader !== null ? Number.parseInt(retryHeader, 10) : undefined;
    // Surface the upstream message (ProblemDetails.detail) so toasts no longer collapse every 503
    // into the generic "Service Unavailable". Fall back to the title or the literal status text
    // when the body is empty or non-conformant.
    const message = isProblemDetails(payload)
      ? (payload.detail ?? payload.title ?? 'Service Unavailable')
      : 'Service Unavailable';
    return new ApiError(503, message, {
      kind: 'unavailable',
      ...(Number.isFinite(retryAfterSeconds) ? { retryAfterSeconds: retryAfterSeconds as number } : {}),
      raw: payload,
    });
  }

  const message = isProblemDetails(payload)
    ? (payload.detail ?? payload.title ?? `HTTP ${status}`)
    : `HTTP ${status}`;
  return new ApiError(status, message, { kind: 'unknown', raw: payload });
}

/**
 * Issues an authenticated request to the DocuSense API.
 *
 * @typeParam T  Expected JSON response shape (use `null` for 204 endpoints).
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { json, body, skipAuth, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  if (!finalHeaders.has('Accept')) {
    finalHeaders.set('Accept', 'application/json');
  }

  let finalBody: BodyInit | undefined = body;
  if (json !== undefined) {
    finalBody = JSON.stringify(json);
    if (!finalHeaders.has('Content-Type')) {
      finalHeaders.set('Content-Type', 'application/json');
    }
  }

  if (skipAuth !== true) {
    const token = TokenStore.get();
    if (token !== null && !finalHeaders.has('Authorization')) {
      finalHeaders.set('Authorization', `Bearer ${token.accessToken}`);
    }
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      ...rest,
      headers: finalHeaders,
      ...(finalBody !== undefined ? { body: finalBody } : {}),
    });
  } catch (cause) {
    throw new ApiError(0, 'Network error', { kind: 'network', raw: cause });
  }

  if (response.ok) {
    if (response.status === 204) {
      return null as T;
    }
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('json')) {
      return (await response.json()) as T;
    }
    // Allow text responses for non-JSON endpoints (rare).
    return (await response.text()) as unknown as T;
  }

  const payload = await readErrorBody(response);
  const error = buildApiError(response.status, payload, response);

  if (error.status === 401) {
    TokenStore.clear();
    dispatchAuthExpired();
  }

  throw error;
}
